import MarketStatDateSnapshotCompetitorsPriceStatistics
	from '@app/model/market_state/MarketStateDailySnapshotCompetitorsPriceStatistics';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import Price from '@app/model/market_state/Price';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CalendarSettingsDefaultDisplayType
	from '@app/model/company_hotel/settings/CalendarSettingsDefaultDisplayType';
import _isEmpty from 'lodash/isEmpty';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import CalendarDayIndicationType
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/fragment/CalendarDayIndicationType';

export class MarketStateStatDateSnapshot {
	marketStateIds: number[]
	statDate: string;
	latestUpdateDate: string;
	competitorsPriceStatistics: MarketStatDateSnapshotCompetitorsPriceStatistics;
	competitorSnapshots: MarketStateStatDateCompetitorSnapshot[];
    weightedAveragePrice?: Price | null;
    roomWeightedAverages?: { roomMappingType: HotelRoomMappingType, price: Price }[];

    public comparisonPrice(companyHotel: CompanyHotel): Price | null | undefined {
        return companyHotel.usesWeightedAverage() ? this.weightedAveragePrice : this.competitorsPriceStatistics?.medianPrice;
    }


	constructor(data: MarketStateStatDateSnapshot) {
		Object.assign(this, data);
		if (data.competitorSnapshots) {
			this.competitorSnapshots = data.competitorSnapshots.map(item => new MarketStateStatDateCompetitorSnapshot(item));
		} else data.competitorSnapshots = [];
	}

	public isEmpty() {
		return _isEmpty(this.competitorSnapshots) || this.competitorSnapshots.every(item => item.isEmpty());
	}

	public findDailyMainHotelSnapshot() {
		return this.competitorSnapshots.find(item => item.isMainHotel);
	}

	public findDistinctRoomMappingTypesForLowestItems(): HotelRoomMappingType[] {
		const roomMappingTypes = new Set<HotelRoomMappingType>();
		this.competitorSnapshots.forEach(item => {
			item.lowestItemIndicesPerRoomMappingType.forEach(lowestItem => {
				roomMappingTypes.add(lowestItem.roomMappingType);
			});
		});
		return Array.from(roomMappingTypes);
	}


	public evaluatePriceMedian(companyHotel: CompanyHotel): [CalendarDayIndicationType] | [] {
		const mainHotelSnapshot = this.findDailyMainHotelSnapshot();
		if (!!mainHotelSnapshot) {
			const lowestItemForRoom = mainHotelSnapshot.getLowestItemForRoom();
			const comparison = this.comparisonPrice(companyHotel);
			if (mainHotelSnapshot.isAvailable() && comparison && comparison.amount > 0) {
				const range = companyHotel.settings.calendar.priceMedianWarningRange;
				const value = Math.ceil(((lowestItemForRoom.lowestItem.item.price.amount / comparison.amount) - 1) * 100);
				const state = value < range.from ? 'success' : value < range.to ? 'warning' : 'critical';

				return [{
					state: state,
					value: (value > 0 ? '+' : '') + value + '%',
					displayType: CalendarSettingsDefaultDisplayType.MEDIAN,
				}];
			}
		}
		return [{
			state: 'none',
			value: companyHotel.usesWeightedAverage() ? 'N/A' : '-',
			displayType: CalendarSettingsDefaultDisplayType.MEDIAN,
		}];
	}

	public evaluatePriceMedianForRoomType(companyHotel: CompanyHotel, roomMappingType: HotelRoomMappingType): [CalendarDayIndicationType] | [] {
		const mainHotelSnapshot = this.findDailyMainHotelSnapshot();
		if (!!mainHotelSnapshot) {
			const lowestItemForRoom = mainHotelSnapshot.getLowestItemForRoomMappingType(roomMappingType);

			const competitorsMedian = companyHotel.usesWeightedAverage()
                ? this.roomWeightedAverages?.find(item => item.roomMappingType === roomMappingType)?.price.amount
                : this.calculateMedianForCompetitorsForRoomType(roomMappingType);

			if (mainHotelSnapshot.isAvailable() && lowestItemForRoom &&lowestItemForRoom.lowestItem && lowestItemForRoom.lowestItem.isAvailable && competitorsMedian) {
				const range = companyHotel.settings.calendar.priceMedianWarningRange;
				const value = Math.ceil(((lowestItemForRoom.lowestItem.item.price.amount / competitorsMedian) - 1) * 100);
				const state = value < range.from ? 'success' : value < range.to ? 'warning' : 'critical';

				return [{
					state: state,
					value: (value > 0 ? '+' : '') + value + '%',
					displayType: CalendarSettingsDefaultDisplayType.MEDIAN,
				}];
			}
		}
		return [{
			state: 'none',
			value: companyHotel.usesWeightedAverage() ? 'N/A' : '-',
			displayType: CalendarSettingsDefaultDisplayType.MEDIAN,
		}];
	}

	public calculateMedianForCompetitorsForRoomType(roomMappingType: HotelRoomMappingType) {
		const competitors = this.competitorSnapshots.filter(item=>!item.isMainHotel);
		const prices = competitors
			.map(item => item.getLowestItemForRoomMappingType(roomMappingType))
			.filter(item=>!!item && item.isAvailable())
			.map(item=>Number(item!.lowestItem.item.price.amount))
			.filter(price => !isNaN(price) && price !== undefined && price !== null);

		if (prices.length === 0) {
			return undefined;
		}

		const sorted = prices.sort((a, b) => a - b);
		const middle = Math.floor(sorted.length / 2);

		if (sorted.length % 2 === 0) {
			return (sorted[middle - 1] + sorted[middle]) / 2;
		} else {
			return sorted[middle];
		}
	}

	public countTotalAvailability() {
		let totalAvailability = 0;
		for (const competitorSnapshot of this.competitorSnapshots) {
			totalAvailability = totalAvailability + competitorSnapshot.lowestItemPerHotelIdAndRoomIds.reduce((sum, room) => sum + room.availability, 0);
		}
		return totalAvailability;
	}

	private evaluateSoldOutCompetitorsCount(): [CalendarDayIndicationType] | [] {
		if (!_isEmpty(this.competitorSnapshots)) {
			const [soldOut, total] = this.countSoldOutOverTotalCompetitors();
			return [{
				state: 'none',
				value: soldOut + '/' + total,
				displayType: CalendarSettingsDefaultDisplayType.SOLD_OUT,
				description: `Sold Out / Total`,
			}];

		}
		return [];
	}

	public countSoldOutOverTotalCompetitors(): [number, number] {
		let total = 0;
		let soldOut = 0;
		this.competitorSnapshots.filter((item) => !item.isMainHotel)
			.forEach(competitorItem => {
				total++;
				if (competitorItem.isSoldOutAndNotLos()) soldOut++;
			});

		return [soldOut, total];
	}

	private evaluatePriceRanking(): [CalendarDayIndicationType] | [] {
		const mainHotelSnapshot = this.findDailyMainHotelSnapshot();
		if (!!mainHotelSnapshot) {
			const value = this.competitorSnapshots.findIndex(item => item.isMainHotel) + 1;
			return [{
				state: 'none',
				value: !mainHotelSnapshot.isAvailable() ? '-/-' : value + '/' + this.competitorSnapshots!.filter(item => item.isAvailable()).length,
				displayType: CalendarSettingsDefaultDisplayType.RANKING,
				description: `Position / Total`,
			}];
		}

		return [];
	}

	public renderIndicator(companyHotel: CompanyHotel): CalendarDayIndicationType[] {
		switch (companyHotel.settings.calendar.defaultDisplayType) {
			case CalendarSettingsDefaultDisplayType.RANKING:
				return this.evaluatePriceRanking();
			case CalendarSettingsDefaultDisplayType.SOLD_OUT:
				return this.evaluateSoldOutCompetitorsCount();

			case CalendarSettingsDefaultDisplayType.NONE:
			default:
				return [];
		}
	}

	findDailyCompetitorSnapshot(competitorId: number) {
		return this.competitorSnapshots.find(item => item.competitorId === competitorId);
	}
}